'use client';

import { c as _c } from "react/compiler-runtime";
import { loadProjectSettings, type RendererType } from '@gnsx/genesys.js';
import { AlertTriangleIcon, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { sdk } from '@/app/sdk';
import { IconPlus, IconSave, IconTrash } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DEFAULT_RENDERER_TYPE } from '@/const';
import { store } from '@/editor/store';
import { updateProjectSettings } from '@/lib/storage/project-settings';
interface ProjectSettingsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}
interface ProjectSettings {
  rendererType?: RendererType;
  [key: string]: string | undefined;
}
interface ProjectNameDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (projectName: string) => void;
}
function ProjectNameDialog(t0) {
  const $ = _c(31);
  const {
    isOpen,
    onOpenChange,
    onConfirm
  } = t0;
  const [projectName, setProjectName] = useState("");
  let t1;
  if ($[0] !== onConfirm || $[1] !== onOpenChange || $[2] !== projectName) {
    t1 = () => {
      if (projectName.trim()) {
        onConfirm(projectName.trim());
        onOpenChange(false);
        setProjectName("");
      }
    };
    $[0] = onConfirm;
    $[1] = onOpenChange;
    $[2] = projectName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleConfirm = t1;
  let t2;
  if ($[4] !== onOpenChange) {
    t2 = () => {
      onOpenChange(false);
      setProjectName("");
    };
    $[4] = onOpenChange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleCancel = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogHeader><DialogTitle>Create Project File</DialogTitle><DialogDescription>No project file exists. Please enter a name for your project.</DialogDescription></DialogHeader>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label htmlFor="project-name">Project Name</Label>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => setProjectName(e.target.value);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== handleConfirm) {
    t6 = e_0 => {
      if (e_0.key === "Enter") {
        handleConfirm();
      }
    };
    $[9] = handleConfirm;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== projectName || $[12] !== t6) {
    t7 = <div className="space-y-4"><div className="space-y-2">{t4}<Input id="project-name" value={projectName} onChange={t5} placeholder="Enter project name" onKeyDown={t6} /></div></div>;
    $[11] = projectName;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== handleCancel) {
    t8 = <Button onClick={handleCancel}>Cancel</Button>;
    $[14] = handleCancel;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== projectName) {
    t9 = projectName.trim();
    $[16] = projectName;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = !t9;
  let t11;
  if ($[18] !== handleConfirm || $[19] !== t10) {
    t11 = <Button onClick={handleConfirm} disabled={t10}>Create Project</Button>;
    $[18] = handleConfirm;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t8) {
    t12 = <DialogFooter>{t8}{t11}</DialogFooter>;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t7) {
    t13 = <DialogContent className="sm:max-w-md">{t3}{t7}{t12}</DialogContent>;
    $[24] = t12;
    $[25] = t7;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== isOpen || $[28] !== onOpenChange || $[29] !== t13) {
    t14 = <Dialog open={isOpen} onOpenChange={onOpenChange}>{t13}</Dialog>;
    $[27] = isOpen;
    $[28] = onOpenChange;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}
function ProjectSettingsDialogContent({
  isOpen,
  onOpenChange
}: ProjectSettingsDialogProps) {
  const [isLoadingProjectData, setIsLoadingProjectData] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasProjectFile, setHasProjectFile] = useState(false);
  const [settings, setSettings] = useState<ProjectSettings>({});
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isProjectNameDialogOpen, setIsProjectNameDialogOpen] = useState(false);
  const loadSettings = useCallback(async () => {
    setIsLoadingProjectData(true);
    setError(null);
    try {
      const projectProvider = store.project.makeScopedStorageProvider();
      const [projectFile, projectSettings] = await loadProjectSettings(projectProvider, store.project.projectFilePathWithProjectPrefix);
      if (projectFile) {
        console.log('loadSettings found projectFile:', projectFile, projectSettings);
        setHasProjectFile(true);
        setSettings(projectSettings as ProjectSettings ?? {});
      } else {
        setHasProjectFile(false);
        setSettings({});
      }
    } catch (err) {
      console.error('Failed to load project settings:', err);
      setError('Failed to load project settings');
    } finally {
      setIsLoadingProjectData(false);
    }
  }, []);
  const saveSettingsWithProjectName = async (projectName?: string) => {
    setIsSaving(true);
    setSaveStatus('');
    setError(null);
    try {
      // Ensure rendererType is set when creating new project
      const settingsToSave = projectName && !settings.rendererType ? {
        ...settings,
        rendererType: DEFAULT_RENDERER_TYPE
      } : settings;
      const projectProvider_0 = store.project.makeScopedStorageProvider();
      const success = await updateProjectSettings(projectProvider_0, settingsToSave, {
        merge: false,
        projectNameToCreateIfNotExist: projectName,
        projectFile: store.project.projectFilePathWithProjectPrefix
      });
      if (success) {
        const isRendererChanged = settings.rendererType && settings.rendererType !== store.scene.rendererType;
        if (isRendererChanged) {
          // Full page reload is required for renderer switch - dynamic switching is too complex
          setSaveStatus('Settings saved. Reloading with new renderer...');
          window.location.reload();
        } else {
          setSaveStatus('Settings saved successfully');
          setHasProjectFile(true);
          // Clear status after 3 seconds
          setTimeout(() => setSaveStatus(''), 3000);
        }
      } else {
        setError('Failed to save settings');
      }
    } catch (err_0) {
      console.error('Failed to save project settings:', err_0);
      setError('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveSettings = () => {
    if (hasProjectFile) {
      // Project file exists, save directly
      saveSettingsWithProjectName();
    } else {
      // No project file, ask for project name
      setIsProjectNameDialogOpen(true);
    }
  };
  const handleProjectNameConfirm = (projectName_0: string) => {
    saveSettingsWithProjectName(projectName_0);
  };
  const handleSettingChange = (key: string, value: string | undefined) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleRendererTypeChange = (value_0: RendererType) => {
    setSettings(prev_0 => ({
      ...prev_0,
      rendererType: value_0
    }));
  };
  const handleKeyChange = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    setSettings(prev_1 => {
      const newSettings = {
        ...prev_1
      };
      if (newKey && !newSettings.hasOwnProperty(newKey)) {
        newSettings[newKey] = newSettings[oldKey] ?? '';
        delete newSettings[oldKey];
      }
      return newSettings;
    });
  };
  const addNewSetting = () => {
    const newKey_0 = `setting_${Date.now()}`;
    setSettings(prev_2 => ({
      ...prev_2,
      [newKey_0]: ''
    }));
  };
  const removeSetting = (key_0: string) => {
    setSettings(prev_3 => {
      const newSettings_0 = {
        ...prev_3
      };
      delete newSettings_0[key_0];
      return newSettings_0;
    });
  };
  useEffect(() => {
    if (isOpen) {
      loadSettings();
    } else {
      // Delay state reset to allow dialog close animation to complete
      const timer = setTimeout(() => {
        setHasProjectFile(false);
        setSettings({});
        setSaveStatus('');
        setError(null);
        setIsProjectNameDialogOpen(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, loadSettings]);
  if (isLoadingProjectData) {
    return <div className="flex items-center justify-center h-32">
        <p className="text-muted-foreground">Loading project settings...</p>
      </div>;
  }
  if (error) {
    return <div className="flex items-center justify-center h-32 border-2 border-dashed border-red-300 bg-red-50 rounded-lg">
        <div className="text-center">
          <AlertTriangleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
          <p className="text-red-800 font-medium">Error</p>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      </div>;
  }
  const settingsEntries = Object.entries(settings);
  return <>
      <div className="space-y-4">
        {!hasProjectFile && <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
            <p className="text-sm">
              <strong>No project file found.</strong> Settings will be saved to a new project file when you click "Save Settings".
            </p>
          </div>}

        {/* Renderer Type Setting */}
        <div className="p-4 border border-border rounded-lg bg-muted/30">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label htmlFor="renderer-type" className="text-sm font-medium cursor-help">
                    Renderer Type
                  </Label>
                </TooltipTrigger>
                <TooltipContent>
                  <code className="text-xs">rendererType</code>
                </TooltipContent>
              </Tooltip>
              <Select value={settings.rendererType ?? DEFAULT_RENDERER_TYPE} onValueChange={handleRendererTypeChange}>
                <SelectTrigger id="renderer-type" size="sm">
                  <SelectValue placeholder="Select renderer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="webgpu">{'WebGPU (Default)'}</SelectItem>
                  <SelectItem value="webgl">{'WebGL'}</SelectItem>
                  <SelectItem value="auto">{'Auto (WebGPU if available, fallback to WebGL)'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Choose the rendering backend for your game. WebGPU may offer better performance but is experimental and requires browser support.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {settingsEntries.filter(([key_3]) => key_3 !== 'rendererType').length === 0 ? <div className="text-center py-8 text-muted-foreground">
              <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No custom settings configured yet</p>
              <p className="text-sm">Click "Add Setting" to create custom settings</p>
            </div> : settingsEntries.filter(([key_1]) => key_1 !== 'rendererType').map(([key_2, value_1], index) => <div key={index} className="flex items-center gap-2 p-3 border border-border rounded-lg">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label htmlFor={`key-${index}`} className="text-xs text-muted-foreground">
                        Key
                      </Label>
                      <Input id={`key-${index}`} value={key_2} onChange={e => handleKeyChange(key_2, e.target.value)} placeholder="Setting key" className="h-8" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`value-${index}`} className="text-xs text-muted-foreground">
                        Value
                      </Label>
                      <Input id={`value-${index}`} value={value_1 ?? ''} onChange={e_0 => handleSettingChange(key_2, e_0.target.value)} placeholder="Setting value" className="h-8" />
                    </div>
                  </div>
                  <Button variant="destructiveHover" size="sm" onClick={() => removeSetting(key_2)} tooltip="Remove setting" className="self-end mb-0.5">
                    <IconTrash className="size-3.5" />
                  </Button>
                </div>)}
        </div>

        <div className="flex justify-between items-center">
          <Button size="sm" onClick={addNewSetting}>
            <IconPlus className="size-3.5" />
            Add Setting
          </Button>

          <div className="flex items-center gap-2">
            {saveStatus && <Label className="text-sm text-info font-medium">
                {saveStatus}
              </Label>}
            <Button variant="primaryHover" onClick={handleSaveSettings}>
              <IconSave className="size-3.5" />
              Save Settings
            </Button>
          </div>
        </div>
      </div>

      <ProjectNameDialog isOpen={isProjectNameDialogOpen} onOpenChange={setIsProjectNameDialogOpen} onConfirm={handleProjectNameConfirm} />
    </>;
}
export const ProjectSettingsDialog = t0 => {
  const $ = _c(12);
  const {
    isOpen,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogTitle className="flex items-center gap-2"><Settings className="size-4" />Project Settings</DialogTitle>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Changes are applied when you click "Save Settings" and are saved to the project file below:</span>;
    t3 = <br />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TooltipTrigger asChild={true}><b className="cursor-pointer hover:underline" onClick={_temp}>{`${store.project.projectPath ?? ""}`}</b></TooltipTrigger>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogHeader>{t1}<DialogDescription>{t2}{t3}<Tooltip>{t4}<TooltipContent><p>Click to open folder containing project file</p></TooltipContent></Tooltip></DialogDescription></DialogHeader>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isOpen || $[6] !== onOpenChange) {
    t6 = <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">{t5}<div className="flex-1 overflow-y-scroll min-h-[400px]"><ProjectSettingsDialogContent isOpen={isOpen} onOpenChange={onOpenChange} /></div></DialogContent>;
    $[5] = isOpen;
    $[6] = onOpenChange;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isOpen || $[9] !== onOpenChange || $[10] !== t6) {
    t7 = <Dialog open={isOpen} onOpenChange={onOpenChange}>{t6}</Dialog>;
    $[8] = isOpen;
    $[9] = onOpenChange;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};
function _temp() {
  const projectFolder = store.project.projectFolder;
  if (projectFolder) {
    sdk?.os.openPath(projectFolder);
  }
}